/**
 * public_chat_controller.js — floating Chip chat widget for the public site.
 *
 * Anonymous counterpart of ai_chat_controller.js: drives the launcher + panel
 * in templates/components/public_chat_widget.html against the unauthenticated
 * POST /chat/public/stream SSE endpoint. No tools, no approval cards, no
 * model picker — text in, streamed text out.
 *
 * State model (single writer): sessionStorage key `chip_public_chat` holds
 * { session_id, ui_messages, history_blob }. Only this controller writes it.
 * The widget root is data-turbo-permanent, so the element — and therefore
 * this controller INSTANCE — survives Turbo navigations; connect() re-runs on
 * each body swap and must stay idempotent. A hard refresh rebuilds the
 * instance from sessionStorage, so the transcript survives that too.
 *
 * SSE vocabulary (mirrors the server): text_delta {chunk} / done {messages,
 * history} / error {message, code?, faq_url?}. `code: 'capped'` renders the
 * FAQ link; everything else is a plain notice bubble.
 *
 * Markdown renderer is the escape-first subset of ai_chat_controller.js (bold, italic,
 * code, lists, headings, paragraphs) plus internal-path-only links — the
 * entity linkifier and approval cards deliberately do NOT exist here.
 *
 * "Talk to a human" handoff: an inline email form POSTing to
 * /chat/public/handoff with the transcript snapshot. The ONLY place this
 * widget collects contact details, always opt-in, never required for an
 * answer. Handoff state is in-memory only (not persisted to sessionStorage).
 *
 * Analytics: PostHog funnel events, all through _track(). The snippet is
 * production-gated, so window.posthog is undefined in local dev and every
 * capture silently no-ops. Properties carry page/turn metadata only — NEVER
 * the visitor's message text.
 */

import { Controller } from "@hotwired/stimulus";
import { pinToVisibleArea, unpinVisibleArea } from "./_visible_area.js";

    var STORAGE_KEY = 'chip_public_chat';
    // Set once the greeting bubble is dismissed/opened so it stays gone for the
    // rest of the session (survives a hard refresh, unlike the in-memory flag).
    var GREETING_KEY = 'chip_public_greeting_dismissed';
    // Chip only says hi once the visitor is actually invested: both a minimum
    // dwell AND a real engagement signal must land before the bubble pops.
    // Engagement is whichever comes first — scroll depth, cumulative pointer
    // travel, or a touch-scroll. Thresholds are deliberately non-trivial so a
    // background tab or a stray mouse jiggle never triggers it.
    var GREETING_MIN_DWELL_MS = 12000;
    var GREETING_SCROLL_PX = 400;
    var GREETING_MOUSE_PX = 900;

    // Typewriter pump — the model streams in phrase/line-sized chunks, which
    // reads as janky lurches. Deltas feed a character queue that a single rAF
    // pump drains at TYPE_CPS, so the display types out smoothly regardless of
    // how the network delivers. TYPE_MAX_LAG_MS bounds the catch-up: if the
    // backlog would trail the live stream by more than this, the pump speeds up
    // so the typed text never falls further behind. Kept tight (a fraction of a
    // second) so the only added latency is enough to smooth jitter, not buffer:
    // when the model out-paces TYPE_CPS the queue self-stabilises at ~this much
    // backlog and drains at the arrival rate; the tail clears within it too.
    var TYPE_CPS = 120;
    var TYPE_MAX_LAG_MS = 120;

    // Stick-to-bottom tolerance: while the user is within this many px of the
    // bottom, new content auto-scrolls to follow; once they scroll further up
    // (to re-read), we leave their position alone instead of yanking them down.
    var SCROLL_PIN_PX = 48;

    // PostHog funnel event names — SSOT for the funnel definition (the
    // PostHog UI builds on these exact strings: opened → question_sent →
    // cta_clicked → signup). Never rename without migrating that funnel.
    var EV_OPENED = 'public_chat_opened';
    var EV_CHIP_CLICKED = 'public_chat_chip_clicked';
    var EV_QUESTION_SENT = 'public_chat_question_sent';
    var EV_CTA_CLICKED = 'public_chat_cta_clicked';
    var EV_HANDOFF_REQUESTED = 'public_chat_handoff_requested';

    // PAGE_CHIPS — SSOT for per-page suggested questions. Keyed by pathname
    // prefix, first match wins, '/' default last. Wording is tuned to
    // questions the public corpus answers well; never an em dash.
    var VS_NAMES = { tradify: 'Tradify', servicem8: 'ServiceM8', fergus: 'Fergus' };
    var DEFAULT_CHIPS = [
        'What does Chippie actually do?',
        'How much does it cost?',
        'How is Chippie different from Tradify?',
        'Is the free tier actually usable?',
    ];
    function chipsForPath(pathname) {
        pathname = String(pathname || '/');
        if (pathname.indexOf('/pricing') === 0) {
            return [
                'How does the free tier work?',
                'What do I get for $25 a month?',
                'I just got hit with a price rise. How does switching work?',
                'What happens to my data if I leave?',
            ];
        }
        if (pathname.indexOf('/vs/') === 0) {
            var slug = pathname.slice(4).split('/')[0].toLowerCase();
            var name = VS_NAMES[slug];
            return [
                name ? 'How is Chippie different from ' + name + '?'
                     : 'How is Chippie different from other job apps?',
                'What does switching involve?',
                'Is the free tier actually usable?',
            ];
        }
        if (pathname.indexOf('/features') === 0) {
            return [
                'Can I send an invoice from my phone?',
                'Does the Xero integration work today?',
                'How does quote to job to invoice flow work?',
            ];
        }
        if (pathname.indexOf('/blog') === 0) {
            return ['What does Chippie actually do?', 'How much does it cost?'];
        }
        return DEFAULT_CHIPS;
    }

    function escapeHtml(text) {
        if (text == null) return '';
        return String(text)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // Escape-first markdown subset. All model output passes through
    // escapeHtml before any markup is injected, so the only attribute values
    // we ever emit are ones we construct ourselves.
    function renderMarkdown(text) {
        text = String(text == null ? '' : text);
        // Bold field labels glued to the preceding word get their own line
        // (same defensive rule as ai_chat_controller.js).
        text = text.replace(/(\S)(\*\*[^*\n]+:\*\*)/g, '$1\n$2');
        var html = escapeHtml(text);
        html = html.replace(/```([\s\S]*?)```/g, function (_m, code) {
            return '<pre class="pchat-code"><code>' + code + '</code></pre>';
        });
        html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
        html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>');
        // Markdown links, internal paths ONLY: href must start with a single
        // "/" (the (?!\/) kills protocol-relative //host) and the character
        // class admits no quotes, spaces, or ":" — so no scheme can smuggle
        // through even though escapeHtml already ran. Lets Chip link /signup,
        // /pricing, /faq etc. without ever linking off-site.
        html = html.replace(
            /\[([^\]\n]+)\]\((\/(?!\/)[A-Za-z0-9\-._~/?#=&%]*)\)/g,
            function (_m, label, href) {
                // data-chat-cta marks the anchor as a funnel CTA; the action
                // fires the capture, then Turbo handles the navigation.
                return '<a href="' + href + '" class="pchat-link" data-chat-cta' +
                    ' data-action="click->public-chat#ctaClicked">' + label + '</a>';
            }
        );
        // Block pass: headings, ordered/unordered lists, paragraphs.
        var lines = html.split('\n');
        var out = [];
        var listType = null; // null | 'ul' | 'ol'
        var textBuffer = [];
        var orderedSeq = 0;
        function flushText() {
            if (textBuffer.length) {
                out.push('<p class="pchat-p">' + textBuffer.join('<br>') + '</p>');
                textBuffer = [];
            }
        }
        function closeList() {
            if (listType) { out.push('</' + listType + '>'); listType = null; }
        }
        function openList(kind) {
            if (listType && listType !== kind) closeList();
            if (!listType) {
                out.push(kind === 'ol' ? '<ol class="pchat-list">' : '<ul class="pchat-list">');
                listType = kind;
            }
        }
        var orderedRe = /^[\s ]*(\d+)[.)][\s ]+(.*)$/;
        var unorderedRe = /^[\s ]*[-*][\s ]+(.*)$/;
        var headingRe = /^[\s ]*(#{1,3})[\s ]+(.*)$/;
        lines.forEach(function (line) {
            var heading = line.match(headingRe);
            var ordered = line.match(orderedRe);
            var unordered = line.match(unorderedRe);
            if (heading) {
                flushText();
                closeList();
                orderedSeq = 0;
                var level = Math.min(3, heading[1].length) + 2; // # → h3 … ### → h5
                out.push('<h' + level + ' class="pchat-heading">' + heading[2] + '</h' + level + '>');
            } else if (ordered) {
                flushText();
                if (listType !== 'ol') orderedSeq = 0;
                openList('ol');
                orderedSeq += 1;
                out.push('<li class="pchat-step"><span class="pchat-num">' + orderedSeq + '.</span> ' + ordered[2] + '</li>');
            } else if (unordered) {
                flushText();
                openList('ul');
                out.push('<li>' + unordered[1] + '</li>');
            } else if (line.trim() === '') {
                flushText();
            } else {
                flushText();
                closeList();
                orderedSeq = 0;
                textBuffer.push(line);
            }
        });
        flushText();
        closeList();
        return out.join('');
    }

export default class extends Controller {
        static targets = ['panel', 'launcher', 'messages', 'intro', 'input', 'send', 'greeting'];

        connect() {
            this.streamUrl = this.element.dataset.publicChatStreamUrl;
            this.faqUrl = this.element.dataset.publicChatFaqUrl;
            this.handoffUrl = this.element.dataset.publicChatHandoffUrl;
            // turbo-permanent keeps this instance alive across navigations —
            // initialise once, re-render every connect.
            if (!this._state) {
                this._state = this._load();
                this._open = false;
                this._inFlight = false;
                this._streamingText = '';
                this._errors = [];
                // Typewriter pump state: _typeQueue holds characters that have
                // arrived but not yet been typed out; _streamingText is what's
                // on screen. _pendingDone parks the done frame until the queue
                // drains; _typeResolve lets _post() await the animation.
                this._typeQueue = '';
                this._typeRAF = null;
                this._typeCredit = 0;
                this._pendingDone = null;
                this._typeResolve = null;
                this._handoff = { open: false, sending: false, sent: false,
                                  error: null, email: '', message: '' };
                this._openedTracked = false;
            }
            if (!this._onDocKeydown) this._onDocKeydown = this._handleDocKeydown.bind(this);
            document.addEventListener('keydown', this._onDocKeydown);
            // The widget root is turbo-permanent: the element never re-renders
            // on navigation, so turbo:load is the only signal the page (and
            // therefore the right chip set) changed.
            if (!this._onTurboLoad) this._onTurboLoad = this._renderChips.bind(this);
            document.addEventListener('turbo:load', this._onTurboLoad);
            if (!this._onViewport) this._onViewport = this._syncViewport.bind(this);
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', this._onViewport);
                window.visualViewport.addEventListener('scroll', this._onViewport);
            }
            this._renderChips();
            this._render();
            // First connect of the session only — the instance survives Turbo
            // navigations, so the greeting pops once, not on every page change.
            // Arm on every connect: the instance is turbo-permanent, so dwell
            // and engagement accumulate across navigations (which are themselves
            // a strong investment signal). _armGreeting is idempotent.
            this._armGreeting();
        }

        disconnect() {
            document.removeEventListener('keydown', this._onDocKeydown);
            document.removeEventListener('turbo:load', this._onTurboLoad);
            if (this._onViewport && window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this._onViewport);
                window.visualViewport.removeEventListener('scroll', this._onViewport);
            }
            this._teardownGreetingArm();
            this._stopTyping();
        }

        // --- greeting bubble ------------------------------------------------

        // Arm the two gates. The bubble pops only once BOTH a minimum dwell has
        // elapsed AND the visitor has shown real engagement (scroll / pointer
        // travel / touch). Idempotent and re-entrant: called on every connect,
        // dwell is measured from a persistent start stamp and engagement
        // accumulates on the instance, so both survive Turbo navigations.
        // No-ops once shown/dismissed, the panel is open, or a conversation
        // is already in progress (returning visitor).
        _armGreeting() {
            if (!this.hasGreetingTarget) return;
            if (this._greetShown) return;
            if (this._open) return;
            if (this._state.ui_messages.length > 0) return;
            if (this._greetingDismissed()) return;

            if (this._greetStartTs == null) {
                this._greetStartTs = Date.now();
                this._greetDwellDone = false;
                this._greetEngaged = false;
                this._greetMouseTravel = 0;
                this._greetLastXY = null;
                this._onGreetScroll = function () {
                    if (window.scrollY > GREETING_SCROLL_PX) this._markEngaged();
                }.bind(this);
                this._onGreetMove = function (e) {
                    if (this._greetLastXY) {
                        this._greetMouseTravel +=
                            Math.abs(e.clientX - this._greetLastXY[0]) +
                            Math.abs(e.clientY - this._greetLastXY[1]);
                    }
                    this._greetLastXY = [e.clientX, e.clientY];
                    if (this._greetMouseTravel > GREETING_MOUSE_PX) this._markEngaged();
                }.bind(this);
                this._onGreetTouch = function () { this._markEngaged(); }.bind(this);
            }

            // (Re)start the dwell timer with the time still remaining, so a
            // navigation mid-countdown doesn't reset the clock.
            if (!this._greetDwellDone) {
                var remaining = Math.max(
                    0, GREETING_MIN_DWELL_MS - (Date.now() - this._greetStartTs));
                if (this._greetDwellTimer) clearTimeout(this._greetDwellTimer);
                this._greetDwellTimer = window.setTimeout(function () {
                    this._greetDwellDone = true;
                    this._tryShowGreeting();
                }.bind(this), remaining);
            }

            window.addEventListener('scroll', this._onGreetScroll, { passive: true });
            window.addEventListener('mousemove', this._onGreetMove, { passive: true });
            window.addEventListener('touchmove', this._onGreetTouch, { passive: true });

            this._tryShowGreeting();  // both gates may already be satisfied
        }

        _markEngaged() {
            if (this._greetEngaged) return;
            this._greetEngaged = true;
            this._tryShowGreeting();
        }

        _tryShowGreeting() {
            if (!this._greetDwellDone || !this._greetEngaged) return;
            this._teardownGreetingArm();
            if (this._open || this._greetingDismissed() ||
                this._state.ui_messages.length > 0) return;
            this._greetShown = true;
            var el = this.greetingTarget;
            el.hidden = false;
            void el.offsetWidth;  // force reflow so the pop animation plays
            el.classList.add('pchat-greeting--show');
        }

        // Detach the listeners and the dwell timer. Does NOT clear the dwell /
        // engagement state — that persists on the instance so it survives a
        // disconnect/reconnect across a Turbo navigation.
        _teardownGreetingArm() {
            if (this._greetDwellTimer) {
                clearTimeout(this._greetDwellTimer);
                this._greetDwellTimer = null;
            }
            if (this._onGreetScroll) window.removeEventListener('scroll', this._onGreetScroll);
            if (this._onGreetMove) window.removeEventListener('mousemove', this._onGreetMove);
            if (this._onGreetTouch) window.removeEventListener('touchmove', this._onGreetTouch);
        }

        _hideGreeting() {
            this._greetShown = true;  // never re-arm once intentionally hidden
            this._teardownGreetingArm();
            if (!this.hasGreetingTarget) return;
            this.greetingTarget.classList.remove('pchat-greeting--show');
            this.greetingTarget.hidden = true;
        }

        openFromGreeting() {
            this._hideGreeting();
            this._setGreetingDismissed();
            this.open();
        }

        dismissGreeting(event) {
            if (event) event.stopPropagation();
            this._hideGreeting();
            this._setGreetingDismissed();
        }

        _greetingDismissed() {
            try { return sessionStorage.getItem(GREETING_KEY) === '1'; }
            catch (_e) { return false; }
        }

        _setGreetingDismissed() {
            try { sessionStorage.setItem(GREETING_KEY, '1'); }
            catch (_e) { /* private mode / quota, non-fatal */ }
        }

        // The on-screen keyboard shrinks the visible area and leaves the last
        // message below the fold. visualViewport fires resize/scroll on every
        // keyboard show/hide, so scroll the transcript to the bottom each time.
        // Keyboard show/hide on mobile. The CSS bottom-anchors whichever block
        // is visible (intro or transcript), so short content already hugs the
        // input row. This only matters when content OVERFLOWS: scroll to the
        // real bottom — the last message, or the "talk to a human" button in
        // the intro — so the keyboard never covers it. The open phone sheet is
        // also pinned to the visible area (_visible_area.js) so the iOS keyboard
        // cannot push its header off the top; unpinned when closed or wider.
        _syncViewport() {
            if (!this._open || !window.matchMedia('(max-width: 767px)').matches) {
                unpinVisibleArea(this.element);
                return;
            }
            pinToVisibleArea(this.element);
            this._stickBottom();
        }

        // --- analytics ------------------------------------------------------

        // All captures go through here. The PostHog snippet is
        // production-gated, so window.posthog is undefined in local dev —
        // events must silently no-op, never throw.
        _track(name, props) {
            if (typeof window.posthog !== 'undefined') {
                window.posthog.capture(name, props);
            }
        }

        _pagePath() {
            return String(window.location.pathname).slice(0, 300);
        }

        // --- open / close -------------------------------------------------

        toggle() { this._open ? this.close() : this.open(); }

        open() {
            this._open = true;
            this._hideGreeting();
            // First open per page-session only — the instance survives Turbo
            // navigations, so the flag does too; a hard refresh resets it.
            if (!this._openedTracked) {
                this._openedTracked = true;
                this._track(EV_OPENED, { page_path: this._pagePath() });
            }
            this._render();
            this._syncViewport();
            this.inputTarget.focus();
        }

        close() {
            this._open = false;
            this._render();
            this._syncViewport();
        }

        // Reset to a fresh conversation: clears the transcript, the parked
        // history blob, handoff state, and the persisted sessionStorage copy.
        // The panel stays open; the intro and chips reappear because
        // ui_messages is empty again. Ignored mid-turn — _inFlight stays true
        // through the typewriter tail, so this can't strand a running stream.
        newChat() {
            if (this._inFlight) return;
            this._stopTyping();
            this._state = { session_id: null, ui_messages: [], history_blob: '' };
            this._streamingText = '';
            this._typeQueue = '';
            this._pendingDone = null;
            this._errors = [];
            this._handoff = { open: false, sending: false, sent: false,
                              error: null, email: '', message: '' };
            this._save();
            this._render();
            this.inputTarget.focus();
        }

        _handleDocKeydown(e) {
            if (e.key === 'Escape' && this._open) this.close();
        }

        // --- sending --------------------------------------------------------

        onInputKeydown(e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.send();
            }
        }

        send() {
            var text = this.inputTarget.value.trim();
            if (!text) return;
            this.inputTarget.value = '';
            this._ask(text);
        }

        askChip(event) {
            var label = (event.currentTarget.textContent || '').trim();
            this._track(EV_CHIP_CLICKED, {
                chip_label: label,  // our own copy, not visitor input
                page_path: this._pagePath(),
            });
            this._ask(label);
        }

        // CTA anchors inside answers (marked data-chat-cta by the renderer).
        // Capture only — Turbo handles the navigation itself.
        ctaClicked(event) {
            this._track(EV_CTA_CLICKED, {
                target: event.currentTarget.getAttribute('href'),
            });
        }

        _ask(text) {
            if (this._inFlight || !text) return;
            this._errors = [];
            var turnIndex = this._state.ui_messages.filter(function (m) {
                return m.role === 'user';
            }).length;
            this._track(EV_QUESTION_SENT, {
                page_path: this._pagePath(),
                turn_index: turnIndex,
                chars: text.length,  // length only, NEVER the text itself
            });
            this._state.ui_messages.push({ role: 'user', content: text });
            this._save();
            this._post();
        }

        async _post() {
            this._inFlight = true;
            this._streamingText = '';
            this._typeQueue = '';
            this._pendingDone = null;
            // The user just sent — jump to the bottom regardless of where they
            // had scrolled, then stick-to-bottom takes over for the reply.
            this._forceScroll = true;
            this._render();
            try {
                if (!this._state.session_id && window.crypto && window.crypto.randomUUID) {
                    this._state.session_id = window.crypto.randomUUID();
                    this._save();
                }
                var body = {
                    messages: this._state.ui_messages,
                    history: this._state.history_blob || '',
                    page_path: this._pagePath(),
                };
                if (this._state.session_id) body.session_id = this._state.session_id;
                var resp = await fetch(this.streamUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
                    body: JSON.stringify(body),
                });
                if (resp.status === 429) {
                    this._errors.push({ message: "Chip's copping a lot of questions right now. Give it a minute and try again." });
                    return;
                }
                if (!resp.ok || !resp.body) {
                    this._errors.push({ message: 'Chip hit a snag answering that. Give it another go.' });
                    return;
                }
                await this._consumeSse(resp);
                // The network is done, but the typewriter may still be catching
                // up. Wait for it to drain (and finalize the done frame) before
                // tearing down in-flight state, so the answer types out fully.
                await this._awaitTyping();
            } catch (_err) {
                this._stopTyping();
                this._typeQueue = '';
                this._streamingText = '';
                this._pendingDone = null;
                this._errors.push({ message: 'Network hiccup. Check your connection and give it another go.' });
            } finally {
                this._inFlight = false;
                this._render();
                if (this._open) this.inputTarget.focus();
            }
        }

        // Minimal SSE parser (same wire handling as ai_chat_controller.js): frames split
        // on blank lines, `event:` + `data:` fields, JSON data payloads.
        async _consumeSse(resp) {
            var reader = resp.body.getReader();
            var decoder = new TextDecoder('utf-8');
            var buffer = '';
            for (;;) {
                var chunk = await reader.read();
                if (chunk.done) break;
                buffer += decoder.decode(chunk.value, { stream: true });
                var sep;
                while ((sep = buffer.indexOf('\n\n')) !== -1) {
                    var frame = buffer.slice(0, sep);
                    buffer = buffer.slice(sep + 2);
                    var event = 'message';
                    var dataLines = [];
                    frame.split('\n').forEach(function (line) {
                        if (line.indexOf('event:') === 0) event = line.slice(6).trim();
                        else if (line.indexOf('data:') === 0) dataLines.push(line.slice(5).trim());
                    });
                    if (!dataLines.length) continue;
                    var parsed;
                    try { parsed = JSON.parse(dataLines.join('\n')); }
                    catch (_) { continue; }
                    this._handleFrame(event, parsed);
                }
            }
        }

        _handleFrame(event, data) {
            if (event === 'text_delta') {
                this._typeQueue += data.chunk || '';
                this._startTyping();
            } else if (event === 'done') {
                // Park the final frame; the pump applies it once the queue
                // drains so the tail still types out instead of jump-cutting.
                this._pendingDone = data;
                this._startTyping();
            } else if (event === 'error') {
                this._stopTyping();
                this._typeQueue = '';
                this._streamingText = '';
                this._pendingDone = null;
                this._errors.push({
                    message: data.message || 'Chip hit a snag answering that. Give it another go.',
                    code: data.code || null,
                    faqUrl: data.faq_url || (data.code === 'capped' ? this.faqUrl : null),
                });
                this._render();
            }
        }

        // --- "talk to a human" handoff ---------------------------------------

        openHandoff() {
            if (this._handoff.sent) return;
            this._handoff.open = true;
            this._handoff.error = null;
            this._render();
            var emailEl = this.element.querySelector('.pchat-handoff-email');
            if (emailEl) emailEl.focus();
        }

        closeHandoff() {
            this._captureHandoffInputs();
            this._handoff.open = false;
            this._render();
        }

        // Re-renders replace the form's DOM, so current input values are
        // captured into state before any render that could drop them.
        _captureHandoffInputs() {
            var emailEl = this.element.querySelector('.pchat-handoff-email');
            var msgEl = this.element.querySelector('.pchat-handoff-msg');
            if (emailEl) this._handoff.email = emailEl.value;
            if (msgEl) this._handoff.message = msgEl.value;
        }

        async sendHandoff() {
            if (this._handoff.sending) return;
            this._captureHandoffInputs();
            var email = this._handoff.email.trim();
            var message = this._handoff.message.trim();
            if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
                this._handoff.error = 'Please enter a valid email address.';
                this._render();
                return;
            }
            this._handoff.sending = true;
            this._handoff.error = null;
            this._render();
            var pagePath = this._pagePath();
            try {
                var resp = await fetch(this.handoffUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: email,
                        message: message,
                        transcript: this._state.ui_messages,
                        page_path: pagePath,
                    }),
                });
                var data = null;
                try { data = await resp.json(); } catch (_) { /* non-JSON body */ }
                if (resp.ok && data && data.success) {
                    this._handoff.sent = true;
                    this._handoff.open = false;
                    this._track(EV_HANDOFF_REQUESTED, { page_path: pagePath });
                } else if (resp.status === 429) {
                    this._handoff.error = 'Too many requests. Give it a minute and try again.';
                } else {
                    this._handoff.error = (data && data.message) ||
                        'Something went wrong. Please try again.';
                }
            } catch (_err) {
                this._handoff.error = 'Network hiccup. Check your connection and give it another go.';
            } finally {
                this._handoff.sending = false;
                this._render();
            }
        }

        _handoffHtml() {
            var h = this._handoff;
            if (h.sent) {
                return '<div class="pchat-msg pchat-msg--notice">' +
                    'Sorted. One of the founders will reply to you directly.</div>';
            }
            if (!h.open) {
                return '<button type="button" class="pchat-human" ' +
                    'data-action="click->public-chat#openHandoff">Talk to a human</button>';
            }
            return '<div class="pchat-handoff">' +
                '<p>Leave your email and one of the founders will reply to you personally.</p>' +
                '<input type="email" class="pchat-handoff-email" placeholder="you@example.com"' +
                ' autocomplete="email" maxlength="255" aria-label="Your email"' +
                ' value="' + escapeHtml(h.email) + '">' +
                '<textarea class="pchat-handoff-msg" rows="2" maxlength="1000"' +
                ' aria-label="Your message" placeholder="Anything to add? (optional)">' +
                escapeHtml(h.message) + '</textarea>' +
                (h.error ? '<div class="pchat-handoff-error">' + escapeHtml(h.error) + '</div>' : '') +
                '<div class="pchat-handoff-actions">' +
                '<button type="button" class="pchat-handoff-send"' +
                ' data-action="click->public-chat#sendHandoff"' + (h.sending ? ' disabled' : '') + '>' +
                (h.sending ? 'Sending...' : 'Send to the founders') + '</button>' +
                '<button type="button" class="pchat-handoff-cancel"' +
                ' data-action="click->public-chat#closeHandoff">Cancel</button>' +
                '</div></div>';
        }

        // --- rendering ------------------------------------------------------

        // --- typewriter pump ------------------------------------------------

        // Drain _typeQueue into _streamingText one slice per animation frame at
        // a steady rate, so the answer types out smoothly no matter how the
        // network chunks it. Idempotent: a no-op while already running.
        _startTyping() {
            if (this._typeRAF !== null) return;
            var self = this;
            this._typeLast = performance.now();
            var tick = function (now) {
                self._typeRAF = null;
                var dt = now - self._typeLast;
                self._typeLast = now;
                if (self._typeQueue.length) {
                    // Bound the visual lag: if draining at the base rate would
                    // leave the backlog trailing by more than TYPE_MAX_LAG_MS,
                    // raise the rate so it never falls further behind.
                    var cps = TYPE_CPS;
                    var lagMs = (self._typeQueue.length / cps) * 1000;
                    if (lagMs > TYPE_MAX_LAG_MS) {
                        cps = self._typeQueue.length / (TYPE_MAX_LAG_MS / 1000);
                    }
                    self._typeCredit += (dt / 1000) * cps;
                    var n = Math.floor(self._typeCredit);
                    if (n > 0) {
                        self._typeCredit -= n;
                        self._streamingText += self._typeQueue.slice(0, n);
                        self._typeQueue = self._typeQueue.slice(n);
                        self._render();
                    }
                }
                if (self._typeQueue.length) {
                    self._typeRAF = requestAnimationFrame(tick);
                } else if (self._pendingDone) {
                    self._finishStreaming();
                } else {
                    // Caught up mid-stream (or done already finalized): idle the
                    // pump and release anyone awaiting the drain.
                    self._typeCredit = 0;
                    self._resolveTyping();
                }
            };
            this._typeRAF = requestAnimationFrame(tick);
        }

        _stopTyping() {
            if (this._typeRAF !== null) {
                cancelAnimationFrame(this._typeRAF);
                this._typeRAF = null;
            }
            this._typeCredit = 0;
            this._resolveTyping();
        }

        // Apply the parked done frame once the queue has fully typed out.
        _finishStreaming() {
            var data = this._pendingDone;
            this._pendingDone = null;
            this._streamingText = '';
            this._typeQueue = '';
            if (Array.isArray(data.messages) && data.messages.length) {
                this._state.ui_messages = data.messages;
            }
            this._state.history_blob = data.history || '';
            this._save();
            this._render();
            this._resolveTyping();
        }

        // Resolves once the pump is idle — the queue is drained and any pending
        // done frame finalized. _post() awaits this before tearing down.
        _awaitTyping() {
            if (this._typeRAF === null && !this._typeQueue.length && !this._pendingDone) {
                return Promise.resolve();
            }
            var self = this;
            return new Promise(function (resolve) { self._typeResolve = resolve; });
        }

        _resolveTyping() {
            if (this._typeResolve) {
                var resolve = this._typeResolve;
                this._typeResolve = null;
                resolve();
            }
        }

        // Swap the suggested chips to match the current page — but only while
        // the widget is still in intro state. An in-progress conversation
        // never gets its chips changed under the visitor.
        _renderChips() {
            if (this._state.ui_messages.length > 0) return;
            var box = this.element.querySelector('.pchat-chips');
            if (!box) return;
            box.innerHTML = chipsForPath(window.location.pathname)
                .map(function (q) {
                    return '<button type="button" data-action="click->public-chat#askChip">' +
                        escapeHtml(q) + '</button>';
                })
                .join('');
        }

        _render() {
            this.element.classList.toggle('pchat--open', this._open);
            this.panelTarget.hidden = !this._open;
            this.launcherTarget.setAttribute('aria-expanded', this._open ? 'true' : 'false');
            this.introTarget.hidden = this._state.ui_messages.length > 0;
            this.sendTarget.disabled = this._inFlight;
            this._renderTranscript();
        }

        _renderTranscript() {
            var parts = [];
            var hasAnswer = false;
            this._state.ui_messages.forEach(function (m) {
                if (m.role === 'user' && m.content) {
                    parts.push('<div class="pchat-msg pchat-msg--user">' + escapeHtml(m.content) + '</div>');
                } else if (m.role === 'assistant' && m.content) {
                    hasAnswer = true;
                    parts.push('<div class="pchat-msg pchat-msg--assistant">' + renderMarkdown(m.content) + '</div>');
                }
            });
            if (this._streamingText) {
                parts.push(
                    '<div class="pchat-msg pchat-msg--assistant pchat-msg--streaming">' +
                    renderMarkdown(this._streamingText) +
                    '</div>'
                );
            } else if (this._inFlight) {
                parts.push(
                    '<div class="pchat-typing" aria-label="Chip is typing">' +
                    '<span></span><span></span><span></span></div>'
                );
            }
            this._errors.forEach(function (err) {
                var inner = escapeHtml(err.message);
                if (err.faqUrl) {
                    inner += ' <a href="' + escapeHtml(err.faqUrl) + '" class="pchat-link">Read the FAQ</a>';
                }
                parts.push('<div class="pchat-msg pchat-msg--notice">' + inner + '</div>');
            });
            // Handoff affordance after any answer (the intro carries its own
            // static button); the open form / success notice always render.
            if (this._handoff.sent || this._handoff.open ||
                (hasAnswer && !this._inFlight && !this._streamingText)) {
                parts.push(this._handoffHtml());
            }
            // Measure pin state BEFORE the swap: afterwards scrollHeight jumps
            // but scrollTop holds, so only the pre-mutation reading reflects
            // where the user actually was.
            var stick = this._forceScroll || this._isPinned();
            this.messagesTarget.innerHTML = parts.join('');
            if (stick) this._stickBottom();
            this._forceScroll = false;
        }

        // .pchat-body owns overflow-y, not the .pchat-messages target inside it.
        _scroller() {
            return this.messagesTarget.parentElement;
        }

        _isPinned() {
            var s = this._scroller();
            if (!s) return true;
            return s.scrollHeight - s.scrollTop - s.clientHeight <= SCROLL_PIN_PX;
        }

        _stickBottom() {
            var s = this._scroller();
            if (s) s.scrollTop = s.scrollHeight;
        }

        // --- sessionStorage (single writer) ----------------------------------

        _load() {
            try {
                var raw = window.sessionStorage.getItem(STORAGE_KEY);
                if (raw) {
                    var parsed = JSON.parse(raw);
                    return {
                        session_id: typeof parsed.session_id === 'string' ? parsed.session_id : null,
                        ui_messages: Array.isArray(parsed.ui_messages) ? parsed.ui_messages : [],
                        history_blob: typeof parsed.history_blob === 'string' ? parsed.history_blob : '',
                    };
                }
            } catch (_) { /* corrupt / unavailable storage — start fresh */ }
            return { session_id: null, ui_messages: [], history_blob: '' };
        }

        _save() {
            try {
                window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this._state));
            } catch (_) { /* private browsing quota — chat still works in memory */ }
        }
}
